import React, { useState, useEffect } from "react";
import { Modal, Button } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Package,
  BookText,
  Calendar,
  Minus,
} from "lucide-react";
import { TiLeaf } from "react-icons/ti";
import "./my-orders.css";
import Loading from "../loading/Loading";

const MyOrders = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("patient"); // Default to patient
  const [isLoadingUserRole, setIsLoadingUserRole] = useState(true);
  const [userRoleError, setUserRoleError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpened, setModalOpened] = useState(false);
  const [filters, setFilters] = useState({
    ordered: false,
    scheduled: false,
    collected: false,
    inTransit: false,
    testInProgress: false,
    resultReady: false,
    dateRangeStart: "DD/MM/YYYY",
    dateRangeEnd: "DD/MM/YYYY",
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(6);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageInput, setPageInput] = useState("1");
  const [dateError, setDateError] = useState("");
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const [pendingResultsCount, setPendingResultsCount] = useState(0);

  const ORDERS_API_ENDPOINT = "/wp-json/dbsb/v1/orders/list";
  const USER_API_ENDPOINT = "/wp-json/dbsb/v1/user/ui-data";

  // Fetch user role from API
  useEffect(() => {
    const fetchUserRole = async () => {
      setIsLoadingUserRole(true);
      setUserRoleError(null);
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(USER_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "You must be logged in to access user data. Please log in and try again."
            );
          } else if (response.status === 403) {
            throw new Error("You don't have permission to access user data.");
          }
          throw new Error("Failed to fetch user data");
        }

        const data = await response.json();
        setUserRole(data.role === "author" ? "provider" : "patient");
      } catch (error) {
        console.error("Error fetching user role:", error);
        setUserRoleError(error.message);
        setUserRole("patient"); // Default to patient on error
      } finally {
        setIsLoadingUserRole(false);
      }
    };

    fetchUserRole();
  }, []);

  // Fetch all orders from API on mount
  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      setError(""); // Clear previous errors
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(`${ORDERS_API_ENDPOINT}?per_page=100`, { // Fetch all orders upfront
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "You must be logged in to view orders. Please log in and try again."
            );
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view orders.");
          }
          throw new Error("Failed to fetch orders. Please try again.");
        }

        const data = await response.json();

        // Map API data to component's expected structure
        const mappedOrders = data.orders.map((order) => ({
          orderId: order.order_id.toString(),
          patientName: order.patient_name || "Unknown",
          testName: order.test_name || "Unknown",
          orderDate: new Date(order.date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          status: order.status.charAt(0).toUpperCase() + order.status.slice(1), // Capitalize status
        }));

        setOrders(mappedOrders);
        setFilteredOrders(mappedOrders);
        setTotalOrdersCount(data.total);
        setPendingResultsCount(data.pending_results);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError(err.message);
        setOrders([]);
        setFilteredOrders([]);
        setTotalOrdersCount(0);
        setPendingResultsCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, []); // Empty dependency array to fetch only on mount

  // Set initial load to false after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Apply filters and search
  useEffect(() => {
    applyFilters();
  }, [orders, searchQuery, filters]);

  // Validate dates when tempFilters change
  useEffect(() => {
    validateDates();
  }, [tempFilters]);

  const parseDate = (dateStr) => {
    if (dateStr === "DD/MM/YYYY") return null;
    const [day, month, year] = dateStr.split("/").map(Number);
    return new Date(year, month - 1, day);
  };

  const formatOrderDate = (dateStr) => {
    return new Date(dateStr);
  };

  const applyFilters = () => {
    let filtered = orders.filter((order) => {
      const statusMatch =
        (!filters.ordered &&
          !filters.scheduled &&
          !filters.collected &&
          !filters.inTransit &&
          !filters.testInProgress &&
          !filters.resultReady) ||
        (filters.ordered && order.status === "Ordered") ||
        (filters.scheduled && order.status === "Scheduled") ||
        (filters.collected && order.status === "Collected") ||
        (filters.inTransit && order.status === "In transit") ||
        (filters.testInProgress && order.status === "Test in progress") ||
        (filters.resultReady && order.status === "Result ready");

      const searchMatch =
        userRole === "provider"
          ? order.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.patientName.toLowerCase().includes(searchQuery.toLowerCase())
          : order.orderId.toLowerCase().includes(searchQuery.toLowerCase());

      let dateMatch = true;
      const startDate = parseDate(filters.dateRangeStart);
      const endDate = parseDate(filters.dateRangeEnd);
      const orderDate = formatOrderDate(order.orderDate);

      if (startDate && endDate) {
        dateMatch = orderDate >= startDate && orderDate <= endDate;
      } else if (startDate) {
        dateMatch = orderDate >= startDate;
      } else if (endDate) {
        dateMatch = orderDate <= endDate;
      }

      return statusMatch && searchMatch && dateMatch;
    });

    setFilteredOrders(filtered);
    setCurrentPage(1);
    setPageInput("1");
  };

  const validateDates = () => {
    const startDate = parseDate(tempFilters.dateRangeStart);
    const endDate = parseDate(tempFilters.dateRangeEnd);

    if (startDate && endDate && endDate < startDate) {
      setDateError("End date cannot be earlier than start date");
      return false;
    }

    setDateError("");
    return true;
  };

  const isApplyDisabled = () => {
    const startSet = tempFilters.dateRangeStart !== "DD/MM/YYYY";
    const endSet = tempFilters.dateRangeEnd !== "DD/MM/YYYY";
    return (startSet && !endSet) || (!startSet && endSet) || !!dateError;
  };

  const handleFilterApply = () => {
    if (!validateDates()) return;
    setFilters(tempFilters);
    setModalOpened(false);
  };

  const handleFilterReset = () => {
    setTempFilters({
      ordered: false,
      scheduled: false,
      collected: false,
      inTransit: false,
      testInProgress: false,
      resultReady: false,
      dateRangeStart: "DD/MM/YYYY",
      dateRangeEnd: "DD/MM/YYYY",
    });
    setDateError("");
  };

  const handleDateChange = (e, field) => {
    const value = e.target.value;
    if (value) {
      const [year, month, day] = value.split("-");
      const formattedDate = `${day}/${month}/${year}`;
      setTempFilters((prev) => ({
        ...prev,
        [field]: formattedDate,
      }));
    } else {
      setTempFilters((prev) => ({
        ...prev,
        [field]: "DD/MM/YYYY",
      }));
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setPageInput(page.toString());
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    setPageInput(value);
    const page = parseInt(value);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleOrdersPerPageChange = (e) => {
    const value = parseInt(e.target.value);
    setOrdersPerPage(value);
    setCurrentPage(1);
    setPageInput("1");
  };

  const statusClasses = {
    Pending: "status-pending",
    Processing: "status-processing",
    Paid: "status-paid",
    Scheduled: "status-scheduled",
    Collected: "status-collected",
    "In transit": "status-in-transit",
    "Test in progress": "status-test-in-progress",
    Testing: "status-testing",
    in_transit: "status-in-transit",
    result_ready: "status-result-ready",
    Cancelled: "status-cancelled",
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    }),
    hover: {
      backgroundColor: "rgba(58, 63, 106, 0.02)",
      transition: { duration: 0.2 },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.1 } },
  };

  const statVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
      },
    },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
  };

  const checkboxVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    }),
  };

  const searchVariants = {
    rest: { scale: 1, boxShadow: "0px 0px 0px rgba(0, 0, 0, 0)" },
    focus: {
      scale: 1.01,
      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
      transition: { type: "spring", stiffness: 400, damping: 20 },
    },
  };

  const totalOrders = filteredOrders.length;
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage); // Use filteredOrders.length
  const startIndex = (currentPage - 1) * ordersPerPage + 1;
  const endIndex = Math.min(startIndex + ordersPerPage - 1, filteredOrders.length);
  const currentOrders = filteredOrders.slice((currentPage - 1) * ordersPerPage, currentPage * ordersPerPage);

  return (
    <motion.div
      className="my-orders-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h1 variants={headingVariants} initial="hidden" animate="visible">
        My orders
      </motion.h1>
      <motion.p
        className="subtitle"
        variants={headingVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.1 }}
      >
        View and track all test orders in one place.
      </motion.p>

      <motion.div
        className="stats-card"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="stat-item" variants={statVariants}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.6064 2.21C12.2464 1.93 11.7464 1.93 11.3864 2.21C9.48641 3.66 3.87637 8.39 3.90637 13.9C3.90637 18.36 7.53641 22 12.0064 22C16.4764 22 20.1064 18.37 20.1064 13.91C20.1164 8.48 14.4964 3.67 12.6064 2.21Z"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-miterlimit="10"
            />
            <path
              d="M12 2V22"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 18.9587L19.7 15.2188"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 13.9628L19.37 10.3828"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 8.95781L17.03 6.50781"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <span>Ordered tests</span>
          <motion.span className="stat-value" variants={statVariants}>
            {totalOrdersCount}
          </motion.span>
        </motion.div>
        <motion.div className="stat-item" variants={statVariants}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 8.25V18C20 21 18.21 22 16 22H8C5.79 22 4 21 4 18V8.25C4 5 5.79 4.25 8 4.25C8 4.87 8.24997 5.43 8.65997 5.84C9.06997 6.25 9.63 6.5 10.25 6.5H13.75C14.99 6.5 16 5.49 16 4.25C18.21 4.25 20 5 20 8.25Z"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M16 4.25C16 5.49 14.99 6.5 13.75 6.5H10.25C9.63 6.5 9.06997 6.25 8.65997 5.84C8.24997 5.43 8 4.87 8 4.25C8 3.01 9.01 2 10.25 2H13.75C14.37 2 14.93 2.25 15.34 2.66C15.75 3.07 16 3.63 16 4.25Z"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M8 13H12"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M8 17H16"
              stroke="#3A3F6A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <span>Pending results</span>
          <motion.span className="stat-value" variants={statVariants}>
            {pendingResultsCount}
          </motion.span>
        </motion.div>
      </motion.div>

      <motion.div
        className="orders-card"
        variants={itemVariants}
        initial="hidden"
        animate="visible"
      >
        {isLoading || isLoadingUserRole ? (
          <motion.div
            className="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Loading />
          </motion.div>
        ) : error || userRoleError ? (
          <motion.div
            className="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Error: {error || userRoleError}
          </motion.div>
        ) : orders.length === 0 ? (
          <motion.div
            className="no-orders"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/assets/order-logo.svg`}
              alt="No orders"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                delay: 0.1,
                type: "spring",
                stiffness: 200,
              }}
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              No ordered tests
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              All ordered tests will appear here
            </motion.p>
          </motion.div>
        ) : (
          <>
            <motion.div
              className="orders-header"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div
                className="search-bar"
                variants={searchVariants}
                initial="rest"
                whileFocus="focus"
                whileHover="focus"
              >
                <Search size={20} color="#656E71" />
                <input
                  type="text"
                  placeholder={
                    userRole === "provider"
                      ? "Search with patient name or id"
                      : "Search by order id"
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </motion.div>
              <motion.div
                className="pagination-info"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <span>
                  {startIndex}-
                  {endIndex < totalOrdersCount ? endIndex : filteredOrders.length}
                  <span>/{filteredOrders.length} tests</span>
                </span>
                <motion.button
                  className="filter-button"
                  onClick={() => setModalOpened(true)}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.9406 16.3885C11.9406 16.8969 11.6073 17.5635 11.1823 17.8219L10.0073 18.5802C8.91563 19.2552 7.39896 18.4969 7.39896 17.1469V12.6885C7.39896 12.0969 7.06562 11.3385 6.72396 10.9219L3.52394 7.5552C3.09894 7.1302 2.76562 6.38021 2.76562 5.87188V3.93854C2.76562 2.9302 3.52397 2.17188 4.44897 2.17188H15.5656C16.4906 2.17188 17.249 2.9302 17.249 3.8552V5.7052C17.249 6.3802 16.824 7.22187 16.4073 7.63854"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13.3932 14.263C14.866 14.263 16.0599 13.0691 16.0599 11.5964C16.0599 10.1236 14.866 8.92969 13.3932 8.92969C11.9205 8.92969 10.7266 10.1236 10.7266 11.5964C10.7266 13.0691 11.9205 14.263 13.3932 14.263Z"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M16.5599 14.763L15.7266 13.9297"
                      stroke="#31383B"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Filter tests
                </motion.button>
              </motion.div>
            </motion.div>

            <motion.div
              className="orders-table"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div
                className="table-header"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                {userRole === "provider" ? (
                  <>
                    <span>Patient name</span>
                    <span>Test ordered</span>
                    <span>Order date</span>
                  </>
                ) : (
                  <>
                    <span>Order id</span>
                    <span>Test ordered</span>
                    <span>Order date</span>
                  </>
                )}
              </motion.div>
              <motion.div
                className="table-body"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <AnimatePresence>
                  {currentOrders.map((order, index) => (
                    <motion.div
                      key={order.orderId}
                      className="table-row"
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      exit={{
                        opacity: 0,
                        x: 20,
                        transition: { duration: 0.2 },
                      }}
                      whileHover="hover"
                      custom={index}
                    >
                      {userRole === "provider" ? (
                        <div className="patient-info">
                          <motion.span
                            className="patient-name"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 + index * 0.05 }}
                          >
                            {order.patientName}
                          </motion.span>
                          <motion.span
                            className="order-id"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 + index * 0.05 }}
                          >
                            Order id: {order.orderId}
                          </motion.span>
                        </div>
                      ) : (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 + index * 0.05 }}
                        >
                          {order.orderId}
                        </motion.span>
                      )}
                      <motion.span
                        className="order-name"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 + index * 0.05 }}
                      >
                        {order.testName}
                      </motion.span>
                      <motion.span
                        className="order-date"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 + index * 0.05 }}
                      >
                        {order.orderDate}
                      </motion.span>
                      <motion.span
                        className={`status ${statusClasses[order.status]}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 + index * 0.05 }}
                      >
                        {order.status.toUpperCase()}
                      </motion.span>
                      <motion.button
                        className="view-details"
                        onClick={() => navigate(`/my-order/${order.orderId}`)}
                        whileHover="hover"
                        whileTap="tap"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 + index * 0.05 }}
                      >
                        View details
                      </motion.button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </motion.div>

            <motion.div
              className="pagination"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="page-selector">
                <motion.input
                  type="text"
                  value={pageInput}
                  onChange={handlePageInputChange}
                  className={parseInt(pageInput) > totalPages ? "invalid" : ""}
                  whileFocus={{
                    scale: 1.05,
                    boxShadow: "0px 0px 5px rgba(58, 63, 106, 0.2)",
                  }}
                  transition={{ duration: 0.2 }}
                />
                <span>of {totalPages} pages</span>
              </div>
              <div className="page-arrows">
                <motion.button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  variants={buttonVariants}
                  whileHover={currentPage !== 1 ? "hover" : {}}
                  whileTap={currentPage !== 1 ? "tap" : {}}
                >
                  <ChevronLeft className="arrow-button-icon" size={18} />
                </motion.button>
                <motion.button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  variants={buttonVariants}
                  whileHover={currentPage !== totalPages ? "hover" : {}}
                  whileTap={currentPage !== totalPages ? "tap" : {}}
                >
                  <ChevronRight className="arrow-button-icon" size={18} />
                </motion.button>
              </div>
              <div className="orders-per-page">
                <span>Orders per page:</span>
                <div className="select-wrapper">
                  <motion.select
                    value={ordersPerPage}
                    onChange={handleOrdersPerPageChange}
                    whileFocus={{ scale: 1.05 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                    <option value={10}>10</option>
                  </motion.select>
                  <ChevronDown size={16} color="#3A3F6A" />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>

      <AnimatePresence>
        {modalOpened && (
          <Modal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            withCloseButton={false}
            centered
            size="sm"
            className="filter-modal"
            overlayProps={{
              opacity: 0.55,
              blur: 3,
            }}
          >
            <motion.div
              className="modal-content"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.h3 variants={headingVariants}>Filter tests</motion.h3>
              <motion.div
                className="filter-section"
                variants={containerVariants}
              >
                <motion.h4 variants={itemVariants}>Test status</motion.h4>
                <motion.div
                  className="checkbox-group"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {[
                    { label: "Ordered", key: "ordered" },
                    { label: "Scheduled", key: "scheduled" },
                    { label: "Collected", key: "collected" },
                    { label: "In Transit", key: "inTransit" },
                    { label: "Test in Progress", key: "testInProgress" },
                    { label: "Result Ready", key: "resultReady" },
                  ].map((item, index) => (
                    <motion.label
                      key={item.key}
                      className="custom-checkbox"
                      variants={checkboxVariants}
                      custom={index}
                    >
                      <input
                        type="checkbox"
                        checked={tempFilters[item.key]}
                        onChange={(e) =>
                          setTempFilters({
                            ...tempFilters,
                            [item.key]: e.target.checked,
                          })
                        }
                      />
                      <span className="checkmark"></span>
                      <span className="checkbox-text">{item.label}</span>
                    </motion.label>
                  ))}
                </motion.div>
                <motion.h4 variants={itemVariants}>
                  Show by date range
                </motion.h4>
                <motion.div className="date-range" variants={itemVariants}>
                  <div className="date-input">
                    <div className="calendar-icon">
                      <Calendar size={20} color="#2a2d4b" />
                    </div>
                    <motion.input
                      type="date"
                      value={
                        tempFilters.dateRangeStart === "DD/MM/YYYY"
                          ? ""
                          : tempFilters.dateRangeStart
                              .split("/")
                              .reverse()
                              .join("-")
                      }
                      onChange={(e) => handleDateChange(e, "dateRangeStart")}
                      placeholder="DD/MM/YYYY"
                      className="date-picker"
                      whileFocus={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    />
                  </div>
                  <span>
                    <Minus size={16} />
                  </span>
                  <div className="date-input">
                    <div className="calendar-icon">
                      <Calendar size={20} color="#2a2d4b" />
                    </div>
                    <motion.input
                      type="date"
                      value={
                        tempFilters.dateRangeEnd === "DD/MM/YYYY"
                          ? ""
                          : tempFilters.dateRangeEnd
                              .split("/")
                              .reverse()
                              .join("-")
                      }
                      onChange={(e) => handleDateChange(e, "dateRangeEnd")}
                      placeholder="DD/MM/YYYY"
                      className="date-picker"
                      whileFocus={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    />
                  </div>
                </motion.div>
                {dateError && (
                  <motion.span
                    className="error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {dateError}
                  </motion.span>
                )}
              </motion.div>
              <motion.div className="modal-buttons" variants={itemVariants}>
                <Button
                  onClick={handleFilterReset}
                  className="reset-button"
                  component={motion.button}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Reset
                </Button>
                <div className="action-buttons">
                  <Button
                    onClick={() => setModalOpened(false)}
                    className="cancel-button"
                    component={motion.button}
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleFilterApply}
                    className={`apply-button ${
                      isApplyDisabled() ? "disabled" : ""
                    }`}
                    disabled={isApplyDisabled()}
                    component={motion.button}
                    variants={buttonVariants}
                    whileHover={isApplyDisabled() ? {} : "hover"}
                    whileTap={isApplyDisabled() ? {} : "tap"}
                  >
                    Apply filters
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MyOrders;